import type { DisplayMode, EntitlementResponse } from '../types';

/**
 * Calculate usage percentage (0-100)
 */
export function calculateUsagePercentage(entitlement: EntitlementResponse): number {
  // No quota means unlimited (no percentage)
  if (entitlement.quota === null || entitlement.quota === undefined) {
    return 0;
  }

  // No current usage tracked
  if (entitlement.current_usage === null || entitlement.current_usage === undefined) {
    return 0;
  }

  const percentage = (entitlement.current_usage / entitlement.quota) * 100;

  // Clamp to 0-100 range
  return Math.min(100, Math.max(0, percentage));
}

/**
 * Format capability name for display
 * Examples: "notes" → "note generations", "labs" → "lab orders"
 */
export function formatCapabilityName(name: string): string {
  const nameMap: Record<string, string> = {
    notes: 'note generations',
    note_creation: 'note generations',
    labs: 'lab orders',
    lab_order_creation: 'lab orders',
    document_creation: 'document generations',
    form_sending: 'forms sent',
    transcription: 'transcription minutes',
    ehr_integration: 'EHR syncs',
    in_note_training: 'AI training',
    template_creation: 'templates',
    audit_suggestions: 'JotAudit checks',
    ehr_note_copy: 'EHR transfers',
  };

  return nameMap[name.toLowerCase()] || name;
}

/**
 * Format usage text for display
 * Examples:
 * - "3/10 Notes created"
 * - "3 Generations used (Unlimited)"
 */
export function formatUsageText(entitlement: EntitlementResponse): string {
  const usage = entitlement.current_usage ?? 0;
  const quota = entitlement.quota;
  const displayUsage = quota === null || quota === undefined ? usage : Math.min(usage, quota);

  // For note_creation, use "Notes created"
  if (entitlement.capability_name === 'note_creation') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} Notes created`;
    }
    return `${displayUsage}/${quota} Notes created`;
  }

  // For lab_order_creation, use "Lab orders"
  if (entitlement.capability_name === 'lab_order_creation') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} Lab orders`;
    }
    return `${displayUsage}/${quota} Lab orders`;
  }

  // For document_creation, use "Documents"
  if (entitlement.capability_name === 'document_creation') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} Documents`;
    }
    return `${displayUsage}/${quota} Documents`;
  }

  // For form_sending, use "Forms sent"
  if (entitlement.capability_name === 'form_sending') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} Forms sent`;
    }
    return `${displayUsage}/${quota} Forms sent`;
  }

  // For in_note_training, use "AI training"
  if (entitlement.capability_name === 'in_note_training') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} AI training`;
    }
    return `${displayUsage}/${quota} AI training`;
  }

  // For template_creation, use "Templates created"
  if (entitlement.capability_name === 'template_creation') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} Templates created`;
    }
    return `${displayUsage}/${quota} Templates created`;
  }

  // For audit_suggestions, use "JotAudit checks"
  if (entitlement.capability_name === 'audit_suggestions') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} JotAudit checks`;
    }
    return `${displayUsage}/${quota} JotAudit checks`;
  }

  // For ehr_note_copy, use "EHR transfers"
  if (entitlement.capability_name === 'ehr_note_copy') {
    if (quota === null || quota === undefined) {
      return `${displayUsage} EHR transfers`;
    }
    return `${displayUsage}/${quota} EHR transfers`;
  }

  // Default behavior for other capabilities
  const capabilityName = entitlement.capability_description || entitlement.capability_name;

  if (quota === null || quota === undefined) {
    // Unlimited
    if (displayUsage > 0) {
      return `${displayUsage} ${capabilityName} used`;
    }
    return `${capabilityName} (Unlimited)`;
  }

  // Limited quota
  return `${displayUsage}/${quota} ${capabilityName}`;
}

/**
 * Format remaining text for display
 * Examples:
 * - "Only 7 note generations left in Starter Plan"
 * - "Unlimited"
 * - null (if depleted or no quota)
 */
export function formatRemainingText(entitlement: EntitlementResponse): string | null {
  const quota = entitlement.quota;
  const remaining = entitlement.quota_remaining;

  // Unlimited or no quota tracking
  if (quota === null || quota === undefined) {
    return null; // Don't show "Unlimited" text, it's implied
  }

  // Depleted
  if (remaining === 0) {
    return null; // Will show depletion message instead
  }

  if (remaining === null || remaining === undefined) {
    return null;
  }

  const capabilityName = formatCapabilityName(entitlement.capability_name);
  const planName = entitlement.bundle_name;

  return `Only ${remaining} ${capabilityName} left in ${planName} Plan`;
}

/**
 * Format depletion message
 * Example: "You've reached your weekly generation limit"
 */
export function formatDepletionMessage(entitlement: EntitlementResponse): string | null {
  const remaining = entitlement.quota_remaining;

  // Not depleted
  if (remaining === null || remaining === undefined || remaining > 0) {
    return null;
  }

  const period = getUsagePeriodLabel(entitlement.usage_period ?? null);
  const periodText = period ? `${period} ` : '';

  return `You've reached your ${periodText}generation limit`;
}

/**
 * Format reset date for display
 * Example: "Credits reset May 11, 2025"
 */
export function formatResetDate(entitlement: EntitlementResponse): string | null {
  const periodEnd = entitlement.current_period_end;

  // Total/lifetime quotas never reset
  if (entitlement.usage_period === 'total') {
    return null;
  }

  if (!periodEnd || !entitlement.usage_period) {
    return null;
  }

  try {
    const date = new Date(periodEnd);

    // Format as "Month Day, Year"
    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });

    return `Credits reset ${formatter.format(date)}`;
  } catch {
    return null;
  }
}

/**
 * Check if entitlement should show usage UI
 */
export function shouldShowUsage(entitlement: EntitlementResponse): boolean {
  // Show if there's actual quota tracking
  if (entitlement.quota !== null && entitlement.quota !== undefined) {
    return true;
  }

  // Don't show for non-metered capabilities without quota
  if (entitlement.metering_type === 'none') {
    return false;
  }

  return true;
}

/**
 * Check if entitlement is depleted
 */
export function isDepleted(entitlement: EntitlementResponse): boolean {
  const remaining = entitlement.quota_remaining;

  // Unlimited or no quota tracking
  if (entitlement.quota === null || entitlement.quota === undefined || remaining === null) {
    return false;
  }

  return remaining === 0;
}

/**
 * Get usage period label
 */
export function getUsagePeriodLabel(period: string | null): string {
  if (!period || period === 'total') {
    return '';
  }

  return period.toLowerCase();
}

/**
 * Determine display mode based on entitlement configuration
 */
export function getDisplayMode(entitlement: EntitlementResponse): DisplayMode {
  // Has quota limit - show as limited regardless of metering_type
  if (entitlement.quota !== null && entitlement.quota !== undefined && entitlement.quota > 0) {
    return 'limited';
  }

  // No metering at all
  if (entitlement.metering_type === 'none') {
    return 'no-metering';
  }

  // Unlimited
  return 'unlimited';
}

/**
 * Get upgrade message based on capability
 */
export function getUpgradeMessage(entitlement: EntitlementResponse): string {
  const capabilityName = formatCapabilityName(entitlement.capability_name);
  return `Upgrade for unlimited ${capabilityName}`;
}

/**
 * Format trial title for display
 * Example: "Unlimited lab orders during your trial"
 */
export function formatTrialTitle(capabilityName: string): string {
  const displayName = formatCapabilityName(capabilityName);
  return `Unlimited ${displayName} during your trial`;
}

/**
 * Format after trial text for display
 * Returns object with prefix and message for flexible rendering
 * Example: { prefix: 'After trial:', message: 'Monthly lab orders depend on your plan.' }
 */
export function formatAfterTrialText(
  capabilityName: string,
  usagePeriod: string | null,
): { prefix: string; message: string } {
  const afterTrialMap: Record<string, string> = {
    note_creation: 'Monthly note generations depend on your plan.',
    lab_order_creation: 'Monthly lab orders depend on your plan.',
    document_creation: 'Monthly document generations depend on your plan.',
    form_sending: 'Monthly forms sent depend on your plan.',
    in_note_training: 'Monthly AI training depends on your plan.',
    template_creation: 'Template limits depend on your plan.',
    audit_suggestions: 'JotAudit access depends on your plan.',
    ehr_note_copy: 'Monthly EHR transfers depend on your plan.',
  };

  // Use specific mapping if available
  const message = afterTrialMap[capabilityName.toLowerCase()];
  if (message) {
    return { prefix: 'After trial:', message };
  }

  // Fallback for unknown capabilities
  const displayName = formatCapabilityName(capabilityName);
  // For total/lifetime quotas, use different wording
  if (usagePeriod === 'total') {
    return { prefix: 'After trial:', message: `${displayName} limits depend on your plan.` };
  }
  return { prefix: 'After trial:', message: `Monthly ${displayName} depend on your plan.` };
}
